import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Plus, BookMarked, Link as LinkIcon, StickyNote, Trash2, Loader2, ExternalLink, X } from "lucide-react";

export default function TeacherMaterials() {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    subject: "General",
    content_type: "link",
    url: "",
    body: "",
    class_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/materials").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);
  const classNameById = Object.fromEntries(classes.map((c) => [c.id, c.name]));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/materials", { ...form, class_id: form.class_id || "" });
      setForm({ title: "", description: "", subject: "General", content_type: "link", url: "", body: "", class_id: "" });
      setShowNew(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    await api.delete(`/materials/${id}`);
    load();
  };

  return (
    <AppShell title="Study materials" subtitle="Share links and notes with your students.">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm" data-testid="new-material-toggle">
          <Plus className="w-4 h-4" /> {showNew ? "Close" : "Add material"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="qs-card p-6 mb-6 space-y-3" data-testid="new-material-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title (e.g. Algebra Cheatsheet)"
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="material-title"
            />
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Subject"
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="material-subject"
            />
          </div>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Short description (optional)"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="material-description"
          />
          <div className="flex items-center gap-2">
            {["link", "note"].map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setForm({ ...form, content_type: t })}
                className={`px-4 py-2 rounded-xl border text-sm font-semibold transition ${
                  form.content_type === t ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`material-type-${t}`}
              >
                {t === "link" ? "Link" : "Note"}
              </button>
            ))}
          </div>
          {form.content_type === "link" ? (
            <input
              type="url"
              required
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="material-url"
            />
          ) : (
            <textarea
              required
              rows={6}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Type your notes here…"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="material-body"
            />
          )}
          <select
            value={form.class_id}
            onChange={(e) => setForm({ ...form, class_id: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="material-class"
          >
            <option value="">Visible to all students</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <button type="submit" disabled={busy || !form.title} className="btn-primary text-sm" data-testid="material-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Publish
          </button>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="teacher-material-list">
        {items.map((m) => (
          <div key={m.id} className="qs-card p-5" data-testid={`material-card-${m.id}`}>
            <div className="flex items-start justify-between">
              <div className={`w-11 h-11 rounded-xl grid place-items-center ${m.content_type === "link" ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600"}`}>
                {m.content_type === "link" ? <LinkIcon className="w-5 h-5" /> : <StickyNote className="w-5 h-5" />}
              </div>
              <button onClick={() => del(m.id)} className="text-rose-500" data-testid={`delete-material-${m.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{m.title}</h3>
            <p className="text-xs text-slate-500 mt-1">
              {m.subject} · {m.class_id ? classNameById[m.class_id] || "Class" : "All students"}
            </p>
            <p className="mt-2 text-sm text-slate-600 line-clamp-2">{m.description || "—"}</p>
            <div className="mt-3">
              {m.content_type === "link" ? (
                <a href={m.url} target="_blank" rel="noreferrer" className="text-indigo-600 text-sm font-semibold inline-flex items-center gap-1" data-testid={`material-open-${m.id}`}>
                  Open link <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : (
                <button onClick={() => setExpanded(m)} className="text-indigo-600 text-sm font-semibold inline-flex items-center gap-1" data-testid={`material-open-${m.id}`}>
                  Read note <BookMarked className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="col-span-full text-center text-slate-500 py-12" data-testid="no-materials">
            No materials yet — add one to share study resources.
          </p>
        )}
      </div>

      {/* Note viewer modal */}
      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setExpanded(null)} data-testid="material-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100 flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-indigo-600">{expanded.subject}</p>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-1">{expanded.title}</h3>
              </div>
              <button onClick={() => setExpanded(null)} className="text-slate-400 hover:text-slate-800" data-testid="material-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-auto text-slate-700 whitespace-pre-wrap">{expanded.body}</div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
